import rankTypeCounts from '@UI/Pages/Home/DocumentsBlock/Helpers/rankTypeCounts';
import resolveTypeSeries, { type TypeSeries } from '@UI/Pages/Home/DocumentsBlock/Helpers/resolveTypeSeries';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type DocumentTypeCountView from '@UI/Store/Indicators/Views/DocumentTypeCountView';

export type TypeDistributionRow = {
	readonly documentTypeId: number;
	readonly label: string;
	readonly value: number;
	readonly isSelected: boolean;
	readonly isArchived: boolean;
	readonly series: TypeSeries;
};

export type TypeDistribution = {
	readonly rows: readonly TypeDistributionRow[];
	/** Aggregate of the types left out of `rows`; `null` when nothing is hidden. */
	readonly others: { readonly typeCount: number; readonly value: number } | null;
	/** Number of distinct types with at least one document, whatever is visible. */
	readonly totalTypeCount: number;
	readonly maxValue: number;
};

/**
 * Ranks the per-type counts, keeps the `visibleCount` largest and folds the rest into one aggregate,
 * so the rows plus the aggregate always sum to the unfiltered total. A selected type outside the top
 * is added as a visible row. Pass `Number.POSITIVE_INFINITY` to list every type.
 */
const buildTypeDistribution = (
	byType: readonly DocumentTypeCountView[],
	documentTypes: DocumentTypesListView | null,
	selectedTypeId: number | null,
	visibleCount: number,
): TypeDistribution => {
	const ranked: readonly TypeDistributionRow[] = rankTypeCounts(byType, documentTypes).map((entry) => ({
		documentTypeId: entry.documentTypeId,
		label: entry.isArchived ? `${entry.label} · archivé` : entry.label,
		value: entry.value,
		isSelected: entry.documentTypeId === selectedTypeId,
		isArchived: entry.isArchived,
		series: resolveTypeSeries(entry.rank, entry.isArchived),
	}));

	const top = ranked.slice(0, visibleCount);
	const selectedOutsideTop = ranked.slice(visibleCount).filter((row) => row.isSelected);
	const rows = [...top, ...selectedOutsideTop];
	const hidden = ranked.filter((row) => !rows.includes(row));

	const others =
		hidden.length > 0
			? { typeCount: hidden.length, value: hidden.reduce((total, row) => total + row.value, 0) }
			: null;

	return {
		rows,
		others,
		totalTypeCount: ranked.length,
		maxValue: rows.reduce((largest, row) => Math.max(largest, row.value), others?.value ?? 0),
	};
};

export default buildTypeDistribution;
